import { isSSR } from '@front10/utils';

/**
 * Get client width
 */
const clientWidth = () => {
  if (!isSSR()) {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  }
  return 0;
};

/**
 * Get client height
 */
const clientHeight = () => {
  if (!isSSR()) {
    return (
      window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
    );
  }
  return 0;
};

/**
 * Define if window is a mobile
 */
const isViewportMobile = () => clientWidth() <= 992;

const truncateNumber = (number, decimalTrunc) => {
  const numberStr = number.toString();
  const pos = numberStr.toString().indexOf('.');

  if (pos !== -1) {
    return numberStr.slice(0, pos + decimalTrunc + 1);
  }

  return numberStr;
};

// This function takes a date that comes from the API and tries
// to convert it to the mm/dd/yyyy format if the function fails
// because the format in which the API date comes is not as expected
// then the date is returned in its original form

// IMPORTANT: At this moment the function assumes that the
// date comes from the API in format yyyy/mm/dd
// Improve the function to check the datePattern that is requested to the API
const transformDateFromAPI = (dateAPI) => {
  if (dateAPI) {
    const splitDate = dateAPI.split('-');

    if (splitDate.length === 3) {
      return `${splitDate[1]}/${splitDate[2]}/${splitDate[0]}`;
    }
  }
  return dateAPI;
};

/**
 * Return true if obj is empty
 * @param {*} obj
 */
const isEmpty = (obj) => Object.entries(obj).length === 0 && obj.constructor === Object;

/**
 * Return true if item is an object
 * @param {*} item
 */
const isObject = (item) => item && typeof item === 'object' && !Array.isArray(item);

/**
 * Deep merge between two objects
 * @param {Obj} target
 * @param {Obj} source
 */
const mergeDeep = (target, ...sources) => {
  if (!sources.length) {
    return target;
  }
  const source = sources.shift();
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    });
  }
  return mergeDeep(target, ...sources);
};

export function debounce(func, wait, immediate) {
  let timeout;
  return (...args) => {
    const later = () => {
      timeout = null;
      if (!immediate) {
        func(...args);
      }
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) {
      func(...args);
    }
  };
}

export function strFormat(format, params) {
  const regex = new RegExp(
    Object.keys(params)
      .map((el) => `\\{${el}\\}`)
      .join('|'),
    'g'
  );

  return format.replace(regex, (match) => params[match.substr(1, match.length - 2)]);
}

export function strFormatV2(format, params) {
  const newParams = { ...params };
  Object.keys(newParams).forEach((i) => {
    if (typeof newParams[i] === 'object' && !Array.isArray(newParams[i])) {
      if (newParams[i] !== null && newParams[i] !== undefined) {
        Object.keys(newParams[i]).forEach((key) => {
          newParams[`${i}.${key}`] = newParams[i][key];
        });
      }
    }
  });
  return strFormat(format, newParams).replace(new RegExp(/{[a-zA-Z]+(.[a-zA-Z]+)*}/g), '');
}

export function strSplitFormat(format, ...args) {
  return format.split(/{(\d+)}/g).map((item) => args[item] || item);
}

export function encodeUrlQueryData(data, separator = '&') {
  return Object.keys(data)
    .filter((key) => data[key] !== undefined)
    .map((key) => [key, data[key]].map(encodeURIComponent).join('='))
    .join(separator);
}

export function getCurrentOriginUrl(defaultUrl) {
  if (!isSSR()) {
    return window.location.origin || `${window.location.protocol}//${window.location.host}`;
  }
  return defaultUrl || '';
}

/**
 * @param v {any} The value to be tested
 * @returns true if this value is nil (null or undefined)
 * as opposed to !v which test if it is falsy. False otherwise.
 */
export const isNil = (v) => v === null || v === undefined;

/**
 * @param v {any} The value to be tested
 * @returns true if this value is not nil (null or undefined)
 * as opposed to !!v which test if it is truthy. False otherwise.
 */
export const isNotNil = (v) => v !== null && v !== undefined;

export const isNilOrEmpty = (v) =>
  v === null || v === undefined || v.length === 0 || Object.keys(v).length === 0;

const getValue = (obj, path) => {
  const splittedPath = path.split('.');
  for (let i = 0; i < splittedPath.length; i += 1) {
    // eslint-disable-next-line no-param-reassign
    obj = obj[splittedPath[i]];
    if (!obj || obj === -1) {
      return undefined;
    }
  }
  return obj;
};

const getQueryParams = (query) => {
  const vars = query.split('&');
  const queryString = {};
  for (let i = 0; i < vars.length; i += 1) {
    try {
      const pair = vars[i].split('=');
      const key = decodeURIComponent(pair[0]);
      const value = decodeURIComponent(pair[1]);

      if (validateParamValue(key, value)) {
        // If first entry with this name
        if (typeof queryString[key] === 'undefined') {
          queryString[key] = decodeURIComponent(value);
          // If second entry with this name
        } else if (typeof queryString[key] === 'string') {
          const arr = [queryString[key], decodeURIComponent(value)];
          queryString[key] = arr;
          // If third or later entry with this name
        } else {
          queryString[key].push(decodeURIComponent(value));
        }
      }
    } catch (err) {
      // Malformed URL error: Ignore parameter for the query string
    }
  }
  return queryString;
};

/**
 * @param {string} key
 * @param {string} value
 */
const validateParamValue = (key, value) => {
  if (key.toLowerCase() === 'utm_campaign') {
    return value.match(new RegExp(/^([a-zA-Z]|[0-9]|[_-]){1,25}$/));
  }

  return true;
};

const mapObject = (obj, mapFn) => {
  if (obj) {
    const newObj = {};
    Object.keys(obj).forEach((key) => {
      newObj[key] = mapFn(obj[key]);
    });
    return newObj;
  }
  return obj;
};

/**
 * It replaces the occurrence of a string is a text string.
 * @param {*} str Text string
 * @param {*} find Text substring to search
 * @param {*} replace Text string to replace
 */
const replaceAll = (str, find, replace) => {
  return str.replace(new RegExp(find, 'g'), replace);
};

function isCharacterALetter(char) {
  return /[a-zA-Z]/.test(char);
}

function isCharacterANumber(char) {
  return /[0-9]/.test(char);
}

export function removeInvalidCharacters(dateFormat) {
  let newDateFormat = '';
  for (let i = 0; i < dateFormat.length; i++) {
    const element = dateFormat[i];
    if (
      isCharacterALetter(element) ||
      isCharacterANumber(element) ||
      element === '-' ||
      element === '_'
    ) {
      newDateFormat = newDateFormat.concat(element);
    }
  }
  return newDateFormat;
}

export const transformNumbersDays = (date) => {
  let numbersDays = 0;
  const number = date.slice(0, -1);
  const string = date.slice(-1);
  switch (string) {
    case 'd':
      numbersDays = number;
      break;
    case 'm':
      numbersDays = number * 30;
      break;

    default:
      break;
  }
  return numbersDays;
};

export function removeTagsHtml(str) {
  if (str !== null || str !== '') {
    // Regular expression to identify HTML tags in
    // the input string. Replacing the identified
    // HTML tag with a null string.
    return str.toString().replace(/(<([^>]+)>)/gi, '');
  }
  return str;
}

const journeyTypeToShortValue = () => ({
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
});

const journeyTypeToLongValue = () => ({
  rt: 'ROUND_TRIP',
  ow: 'ONE_WAY',
  mc: 'MULTI_CITY',
  ew: 'EACH_WAY'
});

const journeyTypeSherpa = () => ({
  rt: 'ROUND_TRIP',
  ROUND_TRIP: 'ROUND_TRIP',
  ONE_WAY: 'ONE_WAY_TRIP',
  ow: 'ONE_WAY_TRIP',
  mc: 'MULTI_CITY_TRIP'
});

export {
  mergeDeep,
  transformDateFromAPI,
  truncateNumber,
  clientWidth,
  clientHeight,
  isViewportMobile,
  isEmpty,
  getValue,
  getQueryParams,
  journeyTypeToShortValue,
  journeyTypeToLongValue,
  journeyTypeSherpa,
  mapObject,
  replaceAll
};
